import {
  REFRACTORIES_WITH_CUSTOMIZATION_QUERY_KEY,
  type ErrorResponse
} from '@domain/refractory-product-catalog'
import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient, type UseMutationResult} from '@tanstack/react-query'
import type {AxiosError} from 'axios'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {logger} from '../../utils'

interface DeleteRefractoryVariables {
  refractoryId: number
}

export const useDeleteRefractory = (): UseMutationResult<
  void,
  AxiosError<ErrorResponse>,
  DeleteRefractoryVariables
> => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError<ErrorResponse>, DeleteRefractoryVariables>({
    mutationFn: async ({refractoryId}) => {
      await ApiClient.axiosInstance.delete<void>(
        `/plants/refractory-product-data-catalog/refractories/${refractoryId}`
      )
    },
    onError: (error) => {
      const errorDetails = error.response?.data?.detail ?? error.message ?? error.response?.status
      logger
        .context('useDeleteRefractory')
        .extras({errorDetails})
        .error('Error occurred while deleting refractory')

      spawnErrorToast(t('error.deleteRefractory'))
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === REFRACTORIES_WITH_CUSTOMIZATION_QUERY_KEY ||
          query.queryKey[0] === 'refractories'
      })
      spawnSuccessToast(t('success.deleteRefractory'))
    }
  })
}
